/**
 * The page's side of the vision worker (see vision.worker.ts).
 *
 * Hands a frame over as an ImageBitmap — transferred, not copied — and gets
 * back people and phone candidates. The rules that turn those into decisions
 * live in erase/geometry.ts and run here, on the page, where they are cheap.
 */
import type { Body, Candidate } from '../erase/geometry';
import type { FromVision, ScanKind, ToVision } from './vision.worker';

export interface ScanResult {
  w: number;
  h: number;
  bodies: Body[];
  found: Candidate[];
  raised: boolean;
  ms: number;
}

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, { resolve: (msg: FromVision) => void; reject: (err: Error) => void }>();

function spawn(): Worker {
  const w = new Worker(new URL('./vision.worker.ts', import.meta.url), { type: 'module', name: 'vision' });
  w.onmessage = (e: MessageEvent<FromVision>) => {
    const p = pending.get(e.data.id);
    if (!p) return;
    pending.delete(e.data.id);
    if (e.data.type === 'failed') p.reject(new Error(e.data.message));
    else p.resolve(e.data);
  };
  // A worker that dies — MediaPipe aborting its WASM is the realistic case —
  // fails everything in flight and is replaced on the next request.
  w.onerror = (e) => {
    const err = new Error(e.message || 'The vision worker stopped.');
    for (const p of pending.values()) p.reject(err);
    pending.clear();
    w.terminate();
    if (worker === w) worker = null;
  };
  return w;
}

/** `Omit` that distributes over a union, so each message keeps its own fields. */
type Unsent<T> = T extends unknown ? Omit<T, 'id'> : never;

function send(msg: Unsent<ToVision>, transfer: Transferable[] = []): Promise<FromVision> {
  worker ??= spawn();
  const id = nextId++;
  return new Promise((resolve, reject) => {
    pending.set(id, { resolve, reject });
    worker!.postMessage({ ...msg, id } as ToVision, transfer);
  });
}

/** Look at a frame: who is there, and what might be a phone. */
export async function scan(source: HTMLCanvasElement, kind: ScanKind): Promise<ScanResult> {
  const image = await createImageBitmap(source);
  const res = await send({ type: 'scan', kind, image }, [image]);
  if (res.type !== 'scanned') throw new Error('Unexpected reply from the vision worker.');
  const { w, h, bodies, found, raised, ms } = res;
  return { w, h, bodies, found, raised, ms };
}

/** A person mask, 0–1 per pixel, for the focal plane; null if it could not be made. */
export async function personMask(source: HTMLCanvasElement): Promise<Float32Array | null> {
  try {
    const image = await createImageBitmap(source);
    const res = await send({ type: 'mask', image }, [image]);
    return res.type === 'masked' ? res.mask : null;
  } catch {
    return null;
  }
}

/** Start the downloads and compiles early, so the first look does not wait on them. */
export function warmVision(kind: ScanKind = 'coach'): void {
  void send({ type: 'warm', kind }).catch(() => undefined);
}
